























































































































































































































































































import {
  defineComponent,
  PropType,
  computed,
  ref,
  watch,
  inject,
} from '@vue/composition-api';
import { Word, DictionaryWordTranslation, AppliedProperty } from '@oare/types';
import { ReloadKey } from '../../../../index.vue';
import EditTranslations from './components/EditTranslations.vue';
import UtilList from '@/components/UtilList/index.vue';
import sl from '@/serviceLocator';
import PropertiesTree from '@/components/Properties/PropertiesTree.vue';

export default defineComponent({
  name: 'WordGrammar',
  props: {
    word: {
      type: Object as PropType<Word>,
      required: true,
    },
    onlyShowFirstTranslation: {
      type: Boolean,
      default: false,
    },
    allowEditing: {
      type: Boolean,
      default: true,
    },
    updateWordInfo: {
      type: Function as PropType<(newWord: Word) => void>,
      required: false,
    },
  },
  components: {
    EditTranslations,
    UtilList,
    PropertiesTree,
  },
  setup({ word, updateWordInfo }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const store = sl.get('store');
    const router = sl.get('router');
    const reload = inject(ReloadKey);

    const isEditingLemmaProperties = ref(false);
    const isEditingDefinitionTranslations = ref(false);
    const isEditingLemmaTranslations = ref(false);
    const isCommenting = ref(false);
    const commentDialogUuid = ref('');
    const commentDialogItem = ref('');
    const routeName = router.currentRoute.name;

    const partsOfSpeech = computed(() =>
      word.properties.filter(prop => prop.variableName === 'Part of Speech')
    );

    const partsOfSpeechString = computed(() =>
      partsOfSpeech.value
        .map(pos => pos.valAbbreviation || pos.valueName)
        .join(', ')
    );

    const verbalThematicVowelTypes = computed(() =>
      word.properties.filter(
        prop => prop.variableName === 'Verbal Thematic Vowel Type'
      )
    );

    const verbalThematicVowelTypesString = computed(() =>
      verbalThematicVowelTypes.value
        .map(pos => pos.valAbbreviation || pos.valueName)
        .join(', ')
    );

    const specialClassifications = computed(() =>
      word.properties.filter(
        prop => prop.variableName === 'Special Classifications'
      )
    );

    const specialClassificationsString = computed(() =>
      specialClassifications.value
        .map(pos => pos.valAbbreviation || pos.valueName)
        .join(', ')
    );

    const editLemmaPropertiesDialog = ref(false);
    const formComplete = ref(false);

    const properties = ref<AppliedProperty[]>([]);
    const setProperties = (propertyList: AppliedProperty[]) => {
      properties.value = propertyList;
    };

    const addPropertiesKey = ref(false);
    watch(editLemmaPropertiesDialog, () => {
      if (editLemmaPropertiesDialog.value) {
        addPropertiesKey.value = !addPropertiesKey.value;
        properties.value = [];
      }
    });

    const updateLemmaProperties = async () => {
      try {
        await server.editPropertiesByReferenceUuid(
          word.uuid,
          properties.value,
          word.uuid
        );
        actions.showSnackbar(
          `Successfully updated lemma properties for ${word.word}`
        );
        reload && reload();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error updating form parse information.',
          err as Error
        );
      } finally {
        properties.value = [];
      }
    };

    const canEditLemmaProperties = computed(() =>
      store.hasPermission('EDIT_ITEM_PROPERTIES')
    );

    const canEditTranslations = computed(() =>
      store.hasPermission('UPDATE_TRANSLATION')
    );

    const canComment = computed(() => store.hasPermission('ADD_COMMENTS'));

    const updateDefinitionTranslations = (
      newTranslations: DictionaryWordTranslation[]
    ) => {
      if (updateWordInfo) {
        updateWordInfo({
          ...word,
          translationsForDefinition: newTranslations,
        });
      }
    };

    const updateDiscussionLemma = (
      newTranslations: DictionaryWordTranslation[]
    ) => {
      if (updateWordInfo) {
        updateWordInfo({
          ...word,
          discussionLemmas: newTranslations,
        });
      }
    };

    const openComment = (uuid: string, item: string) => {
      commentDialogUuid.value = uuid;
      commentDialogItem.value = item;
      isCommenting.value = true;
    };

    // To avoid circular dependencies
    const commentComponent = computed(() =>
      canComment.value
        ? () => import('@/components/CommentItemDisplay/index.vue')
        : null
    );
    return {
      partsOfSpeech,
      partsOfSpeechString,
      verbalThematicVowelTypes,
      verbalThematicVowelTypesString,
      specialClassifications,
      specialClassificationsString,
      editLemmaPropertiesDialog,
      formComplete,
      properties,
      setProperties,
      addPropertiesKey,
      updateLemmaProperties,
      canEditLemmaProperties,
      canEditTranslations,
      isEditingLemmaProperties,
      isEditingDefinitionTranslations,
      isEditingLemmaTranslations,
      isCommenting,
      commentDialogItem,
      commentDialogUuid,
      commentComponent,
      canComment,
      openComment,
      updateDefinitionTranslations,
      updateDiscussionLemma,
      routeName,
    };
  },
});
